import { useCallback, useEffect, useState } from "react";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Reads the currently-displayed month from the DOM `.mnav-label` label and
 * the currently-active tab from `.tab.active`. The original HR script keeps
 * `curMonth`/`curYear` in a script-scoped `let`, so they aren't on window.
 */
export default function useMonthLock(user) {
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((x) => x + 1), []);

  // Re-render whenever the HR script changes tab / month so the chip stays in sync.
  useEffect(() => {
    function patch() {
      if (typeof window.setTab === "function" && !window.__hh_lock_patched) {
        const origSetTab = window.setTab;
        window.setTab = function (t) {
          origSetTab(t);
          refresh();
        };
        const origPrev = window.prevMonth;
        const origNext = window.nextMonth;
        if (origPrev) {
          window.prevMonth = function () {
            origPrev();
            refresh();
          };
        }
        if (origNext) {
          window.nextMonth = function () {
            origNext();
            refresh();
          };
        }
        window.__hh_lock_patched = true;
        return true;
      }
      return false;
    }
    if (!patch()) {
      const id = setInterval(() => {
        if (patch()) clearInterval(id);
      }, 100);
      return () => clearInterval(id);
    }
  }, [refresh]);

  function currentMonthKey() {
    // Storage key — kept as YYYY-MM so it matches what the legacy HR script
    // writes via window.monthLocked. Display formatting is done in LockChip.
    const label = document.querySelector(".mnav-label")?.textContent?.trim();
    if (!label) return null;
    const parts = label.split(/\s+/);
    if (parts.length < 2) return null;
    const idx = MONTH_NAMES.findIndex(
      (n) => n.toLowerCase() === parts[0].toLowerCase(),
    );
    const y = parseInt(parts[1], 10);
    if (idx < 0 || !y) return null;
    return `${y}-${String(idx + 1).padStart(2, "0")}`;
  }

  function displayMonth(key) {
    // Convert storage key "YYYY-MM" to user-friendly "MM-YYYY"
    if (!key) return "";
    const m = key.match(/^(\d{4})-(\d{2})$/);
    return m ? `${m[2]}-${m[1]}` : key;
  }

  function activeTab() {
    return document.querySelector(".tab.active")?.getAttribute("data-tab") || null;
  }

  function isCurrentMonthLocked() {
    try {
      const locks = JSON.parse(localStorage.getItem("hh3_locks") || "{}");
      const k = currentMonthKey();
      return !!(k && locks[k]);
    } catch (e) {
      console.error("Failed to read lock state:", e);
      return false;
    }
  }

  function lockMonth(key) {
    const locks = JSON.parse(localStorage.getItem("hh3_locks") || "{}");
    locks[key] = {
      locked_at: new Date().toISOString(),
      locked_by: user?.email,
    };
    localStorage.setItem("hh3_locks", JSON.stringify(locks));
    refresh();
    if (window.renderPage) window.renderPage(window.curTab);
  }

  function unlockMonth(key) {
    const locks = JSON.parse(localStorage.getItem("hh3_locks") || "{}");
    delete locks[key];
    localStorage.setItem("hh3_locks", JSON.stringify(locks));
    refresh();
    if (window.renderPage) window.renderPage(window.curTab);
  }

  // Reference tick to force recompute when state changes
  void tick;
  const key = currentMonthKey();
  return {
    monthKey: key,
    monthDisplay: displayMonth(key),
    locked: isCurrentMonthLocked(),
    tab: activeTab(),
    lockMonth,
    unlockMonth,
    refresh,
  };
}
